/**
 * Email Templates - Tree Builder Utility
 *
 * Converts flat folder + template lists into a hierarchical tree structure
 * using folder.parent_id and template.folder_id relationships.
 */

import type { EmailTemplate, TemplateNode } from './types'
import type { EmailFolder } from './folder-types'

/**
 * Builds a tree structure from separate folder and template lists.
 *
 * Node IDs are namespaced to avoid collisions:
 * - Folders: "folder-{id}"
 * - Templates: "template-{templateKey}"
 *
 * Use stripNodePrefix() to extract the actual ID for API calls.
 */
export function buildTemplateTree(
  templates: EmailTemplate[],
  folders: EmailFolder[] = []
): TemplateNode {
  const root: TemplateNode = {
    id: 'root',
    name: 'Email Templates',
    type: 'folder',
    path: '/',
    children: [],
  }

  // Build a map of folder ID → folder for quick lookups
  const folderMap = new Map(folders.map((f) => [f.id, f]))

  // Build folder nodes map (folder ID → TemplateNode)
  const folderNodeMap = new Map<number, TemplateNode>()
  for (const folder of folders) {
    folderNodeMap.set(folder.id, {
      id: `folder-${folder.id}`,
      name: folder.name,
      type: 'folder',
      path: buildFolderPath(folder, folderMap),
      children: [],
    })
  }

  // Attach folder nodes to their parents (or root)
  // Process by level to ensure parents are attached before children
  const sortedFolders = [...folders].sort((a, b) => {
    if (a.level !== b.level) return a.level - b.level
    if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder
    return a.name.localeCompare(b.name)
  })

  for (const folder of sortedFolders) {
    const node = folderNodeMap.get(folder.id)!
    if (folder.parentId && folderNodeMap.has(folder.parentId)) {
      folderNodeMap.get(folder.parentId)!.children!.push(node)
    } else {
      root.children!.push(node)
    }
  }

  // Slot templates into their folders (or root if no folder_id)
  const sortedTemplates = [...templates].sort((a, b) => a.name.localeCompare(b.name))

  for (const template of sortedTemplates) {
    const templateNode: TemplateNode = {
      id: `template-${template.id}`,
      name: template.name,
      type: 'file',
      path: template.path,
      template,
    }

    if (template.folderId && folderNodeMap.has(template.folderId)) {
      folderNodeMap.get(template.folderId)!.children!.push(templateNode)
    } else {
      root.children!.push(templateNode)
    }
  }

  return root
}

/**
 * Build a breadcrumb path for a folder (e.g., "/announcements/q1-2026")
 */
function buildFolderPath(
  folder: EmailFolder,
  folderMap: Map<number, EmailFolder>
): string {
  const parts: string[] = []
  let current: EmailFolder | undefined = folder
  while (current) {
    parts.unshift(slugify(current.name))
    current = current.parentId ? folderMap.get(current.parentId) : undefined
  }
  return '/' + parts.join('/')
}

/**
 * Build a human-readable breadcrumb for a folder (e.g., "Announcements > Q1 2026")
 * Used by FolderSelect component.
 */
export function buildFolderBreadcrumb(
  folderId: number,
  folders: EmailFolder[]
): string {
  const folderMap = new Map(folders.map((f) => [f.id, f]))
  const parts: string[] = []
  let current: EmailFolder | undefined = folderMap.get(folderId)
  while (current) {
    parts.unshift(current.name)
    current = current.parentId ? folderMap.get(current.parentId) : undefined
  }
  return parts.join(' \u2192 ')
}

/**
 * Strips the "folder-" or "template-" prefix from a node ID.
 * Returns { type, id } where type is 'folder' or 'template' and id is the raw value.
 */
export function stripNodePrefix(nodeId: string): { type: 'folder' | 'template' | 'root'; id: string } {
  if (nodeId === 'root') return { type: 'root', id: 'root' }
  if (nodeId.startsWith('folder-')) return { type: 'folder', id: nodeId.slice(7) }
  if (nodeId.startsWith('template-')) return { type: 'template', id: nodeId.slice(9) }
  // Fallback: assume template key (for backwards compat)
  return { type: 'template', id: nodeId }
}

/**
 * Converts a string to a URL-friendly slug
 */
function slugify(str: string): string {
  return str
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
}

/**
 * Formats a key into a display label (e.g., 'non-physician' -> 'Non-Physician')
 */
export function formatLabel(key: string): string {
  return key
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

/**
 * Formats an audience key with special handling for known values
 */
export function formatAudience(audience: string): string {
  if (audience === 'non-physician') return 'Non-Physician'
  if (audience === 'all') return 'All Staff'
  return formatLabel(audience)
}
